/* eslint-disable @typescript-eslint/no-explicit-any */
import { Shadows } from '@mui/material'
import { mixColor } from './colorCodeGenerator'
import { Theme, ThemeOptions } from '@mui/material/styles'
import { createTheme as createMUITheme } from '@mui/material/styles'
import { Components } from '@mui/material/styles/components'
import muiButton from './Components/muiButton'
import muiChip from './Components/muiChip'
import muiIconButton from './Components/muiIconButton'
import muiAlert from './Components/muiAlert'
import muiAutoComplete from './Components/muiAutoComplete'
import muiAvatar from './Components/muiAvatar'
import muiAvatarGroup from './Components/muiAvtargroup'
import muiTypography from './Components/muiTypography'
import muiButtonGrop from './Components/muiButtonGroup'
import muiButtonBase from './Components/muiButtonBase'
import muiCard from './Components/muiCard'
import muiContainer from './Components/muiContainer'
import muiSwitch from './Components/muiSwitch'
import muiRadio from './Components/muiRadio'
import muiCheckbox from './Components/muiCheckbox'
import muiSlider from './Components/muiSlider'
import muiTooltip from './Components/muiTooltip'
import muiTab from './Components/muiTab'
import muiTabs from './Components/muiTabs'
import muiPagination from './Components/muiPagination'
import muiPaginationItem from './Components/muiPaginationItem'
import muiMenu from './Components/muiMenu'
import muiMenuItem from './Components/muiMenuItem'
import muiList from './Components/muiList'
import muiInputAdornment from './Components/muiInputAdornment'
import muiInputLabel from './Components/muiInputLabel'
import muiInputBase from './Components/muiInputBase'
import muiOutlinedInput from './Components/muiOutlinedInput'
import muiInput from './Components/muiInput'
import muiFormLabel from './Components/muiFormLabel'
import muiFormControlLabel from './Components/muiFormControlLabel'
import MuiFormHelperText from './Components/muiFormHelperText'
import muiPaper from './Components/muiPaper'
import muiCardHeader from './Components/muiCardHeader'
import muiCardContent from './Components/muiCardContent'
import muiToolbar from './Components/muiToolbar'
import muiAppBar from './Components/muiAppbar'
import muiDrawer from './Components/muiDrawer'
import muiBadge from './Components/muiBadge'
import muiDivider from './Components/muiDivider'
import muiBackDrop from './Components/muiBackDrop'
import muiDialogTitle from './Components/muiDialogTitle'
import muiSelect from './Components/muiSelect'
import { Plus_Jakarta_Sans } from 'next/font/google'

export const plusJakartaSans = Plus_Jakarta_Sans({
  weight: ['300', '400', '500', '700'],
  subsets: ['latin'],
  display: 'swap'
})

const fontFamily = plusJakartaSans.style.fontFamily

// Design system main colors
const black = '#000000'
const bodyColor = '#FFFFFF'
const accentColor = '#794DED'
// const accentColor = '#7bb225'
const primaryFontColor = '#09131D'

// Sementic colors
const success = '#4CAF50'
const error = '#F44336'
const warning = '#FC9C25'
const info = '#794DED'

const fontWeight = {
  regular: 400,
  medium: 500,
  semiBold: 600,
  bold: 700
}

const componentsConfig = (theme: Theme): Components<Omit<Theme, 'components'>> => ({
  MuiAppBar: muiAppBar(theme) as any,
  MuiAlert: muiAlert(theme) as any,
  MuiTypography: muiTypography(theme) as any,
  MuiAutocomplete: muiAutoComplete(theme) as any,
  MuiAvatar: muiAvatar(theme) as any,
  MuiButton: muiButton(theme) as any,
  MuiButtonGroup: muiButtonGrop(theme) as any,
  MuiCardContent: muiCardContent(theme) as any,
  MuiCardHeader: muiCardHeader(theme) as any,
  MuiBadge: muiBadge(theme) as any,
  MuiAvatarGroup: muiAvatarGroup(theme) as any,
  MuiBackdrop: muiBackDrop(theme) as any,
  MuiButtonBase: muiButtonBase(theme) as any,
  MuiCard: muiCard(theme) as any,
  MuiCheckbox: muiCheckbox(theme) as any,
  MuiSelect: muiSelect(theme) as any,
  MuiChip: muiChip(theme) as any,
  MuiDrawer: muiDrawer(theme) as any,
  MuiDivider: muiDivider(theme) as any,
  MuiFormLabel: muiFormLabel(theme) as any,
  MuiFormControlLabel: muiFormControlLabel(theme) as any,
  MuiFormHelperText: MuiFormHelperText(theme) as any,
  MuiContainer: muiContainer(theme) as any,
  MuiIconButton: muiIconButton(theme) as any,
  MuiInput: muiInput(theme) as any,
  MuiInputAdornment: muiInputAdornment(theme) as any,
  MuiInputBase: muiInputBase(theme) as any,
  MuiInputLabel: muiInputLabel(theme) as any,
  MuiOutlinedInput: muiOutlinedInput(theme) as any,
  MuiList: muiList(theme) as any,
  MuiPaper: muiPaper(theme) as any,
  MuiSwitch: muiSwitch(theme) as any,
  MuiRadio: muiRadio(theme) as any,
  MuiDialogTitle: muiDialogTitle(theme) as any,
  MuiSlider: muiSlider(theme) as any,
  MuiTooltip: muiTooltip(theme) as any,
  MuiTabs: muiTabs(theme) as any,
  MuiTab: muiTab(theme) as any,
  MuiPagination: muiPagination(theme) as any,
  MuiPaginationItem: muiPaginationItem(theme) as any,
  MuiMenu: muiMenu(theme) as any,
  MuiMenuItem: muiMenuItem(theme) as any,
  MuiToolbar: muiToolbar(theme) as any
})

const themeConfig = () => {
  const primaryColorPalette = {
    main: accentColor, // main accent color
    dark: mixColor(black, accentColor, 15), // primary dark
    light: mixColor(accentColor, bodyColor, 15), // primary focused
    contrastText: bodyColor //hover-accent-mix
    // dark: '#5837B0', // primary dark
    // light: '#E1DEFF' // primary focused
  }

  // const secondaryColor = {
  //   contrastText: primaryColorPalette.main // white
  // }

  const textColorPalette = {
    primary: '#09131D',
    secondary: '#787E85',
    disabled: '#91979C'
  }

  const brandPastels = {
    stickyNoteLight: '#F9FAE5',
    hydrationLight: '#F3F6F7',
    glassLight: '#F7F9FA'
  }

  const otherColors = {
    caption: mixColor(primaryFontColor, bodyColor, 60), //797979
    placeholder: '#91979C',
    stroke: '#E9EDF1',
    // stroke: mixColor(primaryFontColor, bodyColor, 9.5),
    accentStroke: mixColor(accentColor, bodyColor, 40), //f4cfbe
    disabled: '#F0F2F5',
    default: '#E5E6EB',
    subHover: mixColor(primaryFontColor, bodyColor, 5) //f4f4f4
  }

  const backgroundColor = {
    default: '#FCFDFF', // Secondary Surface
    paper: bodyColor // Tertiary surface
    // paper: "#F7F9FA", // Tertiary surface
  }

  // Create a theme instance.
  const successColor = {
    main: success,
    light: '#E4F3E5'
  }
  const errorColor = {
    main: error,
    light: '#FDE3E1'
  }
  const warningColor = {
    main: warning,
    light: '#FFF3E5'
  }
  const infoColor = {
    main: info,
    light: primaryColorPalette.light
  }

  const theme: ThemeOptions = {
    palette: {
      primary: primaryColorPalette,
      // secondary: secondaryColor,
      text: {
        primary: textColorPalette.primary,
        secondary: textColorPalette.secondary,
        disabled: textColorPalette.disabled
      },
      success: successColor,
      error: errorColor,
      info: infoColor,
      warning: warningColor,
      action: {
        // hover: otherColors.stroke,
        // disabled: "#F0F2F5",
        disabled: textColorPalette.secondary,
        disabledBackground: '#E5E6EB' //Disabled button BG
        // focus: primaryColorPalette.light,
        // selected: primaryColorPalette.light,
      },
      background: backgroundColor
    },
    colors: {
      // caption: otherColors.caption, //797979
      placeholder: otherColors.placeholder, //a6a6a6
      stroke: otherColors.stroke, //E9EDF1
      // accentStroke: otherColors.accentStroke,
      disabled: otherColors.disabled,
      // default: otherColors.default, //636363
      // subHover: otherColors.subHover, //f4f4f4
      stickyNoteLight: brandPastels.stickyNoteLight,
      hydrationLight: brandPastels.hydrationLight,
      glassLight: brandPastels.glassLight
    },
    shadows: [
      'none',
      '0px 0px 0px 0px rgba(0, 0, 0, 0)',
      '0px 2px 2px 0px rgba(0, 0, 0, 0.25)',
      '0px 6px 8px 0px rgba(0, 0, 0, 0.25)',
      '0px 15px 52px 15px rgba(0,0,0, 0.25)',
      ...Array(20).fill('none')
    ] as Shadows,
    typography: {
      fontFamily: fontFamily,
      fontSize: 16,
      h1: {
        fontSize: '1.625rem', //26px
        fontWeight: fontWeight.bold
      },
      h2: {
        fontSize: '1.5rem', //24px
        fontWeight: fontWeight.semiBold
      },
      h3: {
        fontSize: '1.375rem', //22px
        fontWeight: fontWeight.semiBold
      },
      h4: {
        fontSize: '1.25rem', //20px
        fontWeight: fontWeight.semiBold
      },
      h5: {
        fontSize: '1.125rem', //18px
        fontWeight: fontWeight.semiBold
      },
      h6: {
        fontSize: '1rem', // 16px
        fontWeight: fontWeight.medium
      },
      body2: {
        fontSize: '0.875rem', //14px
        fontWeight: fontWeight.regular
      },
      body1: {
        fontSize: '0.875rem', //14px
        fontWeight: fontWeight.medium
      },
      caption: {
        fontSize: '0.75rem', //12px
        fontWeight: fontWeight.regular
      },
      // Caption small taking as Subtitle2 -  in Hybrid DS Figma mentioned as "Caption small"
      subtitle2: {
        fontSize: '0.625rem', //10px
        fontWeight: fontWeight.regular
      },
      subtitle1: {
        fontSize: '0.6251rem', //10px
        fontWeight: fontWeight.medium
      }
    },
    shape: {
      borderRadius: 2
    }
    // breakpoints: {
    // 	values: {
    // 		xs: 0,
    // 		sm: 600,
    // 		md: 900,
    // 		lg: 1200,
    // 		xl: 1536,
    // 	},
    // },
  }

  const themeTemp = createMUITheme(theme)

  return createMUITheme({
    ...theme,
    components: componentsConfig(themeTemp)
  })
}

export default themeConfig()
